/**
 * PageHeader - Title + optional back link + actions (stacks on mobile)
 */

import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useDeviceType } from "../../../features/leads/hooks/useDeviceType";

const HeaderRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
}));

const TitleBlock = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 2,
  minWidth: 0,
});

const ActionsBlock = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  flexWrap: "wrap",
  width: "100%",
  [theme.breakpoints.up("md")]: {
    width: "auto",
  },
}));

/**
 * @param {Object} props
 * @param {string} props.title
 * @param {React.ReactNode} [props.actions]
 * @param {{ label: string, shortLabel?: string, onClick: Function }} [props.back]
 * @param {string} [props.subtitle]
 */
const PageHeader = ({ title, actions, back, subtitle }) => {
  const { isMobile } = useDeviceType();

  return (
    <HeaderRoot>
      <TitleBlock>
        {back && (
          <Button
            startIcon={<ArrowBack />}
            onClick={back.onClick}
            size="small"
            sx={{
              textTransform: "none",
              px: 0,
              minWidth: 0,
              alignSelf: "flex-start",
            }}
          >
            {isMobile && back.shortLabel ? back.shortLabel : back.label}
          </Button>
        )}
        <Typography
          variant={isMobile ? "h5" : "h4"}
          component="h1"
          sx={{
            fontWeight: 700,
            lineHeight: 1.25,
            wordBreak: "break-word",
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </TitleBlock>
      {actions && <ActionsBlock>{actions}</ActionsBlock>}
    </HeaderRoot>
  );
};

export default PageHeader;
